/**
 * #366 — After webview reload/crash, probe scan status once before refiring
 * get_albums / index loads (prevents post-reload retry storms mid-scan).
 */
import { shouldUseLocalLibraryIpc } from '../services/localLibraryIpcGate';
import {
  isLibraryScanInProgress,
  refreshConnectorScanWalkProgress,
} from '../services/libraryScanCoordinator';

let recoveryGateSatisfied = false;

export function isWebviewRecoveryNavigation(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    if (nav?.type === 'reload') return true;
  } catch {
    /* ignore */
  }
  try {
    if (new URL(window.location.href).searchParams.has('b')) return true;
  } catch {
    /* ignore */
  }
  return false;
}

/** @internal test hook */
export function resetLibraryIndexReloadGateForTests(): void {
  recoveryGateSatisfied = false;
}

export type WebviewRecoveryGateResult = 'proceed' | 'defer_scan_active';

/**
 * On webview reload/crash recovery: one clean scan-status check before index IPC.
 * When a scan is still active, callers defer (scan poll / backoff owns the retry).
 */
export async function awaitCleanScanStatusAfterWebviewRecovery(
  signal?: AbortSignal,
): Promise<WebviewRecoveryGateResult> {
  if (!shouldUseLocalLibraryIpc()) return 'proceed';
  if (recoveryGateSatisfied) return 'proceed';
  if (!isWebviewRecoveryNavigation()) {
    recoveryGateSatisfied = true;
    return 'proceed';
  }

  if (signal?.aborted) {
    throw new DOMException('Aborted', 'AbortError');
  }

  await refreshConnectorScanWalkProgress();

  if (isLibraryScanInProgress()) {
    console.info(
      '[library-grid] webview recovery — scan active after status check; deferring index load',
    );
    return 'defer_scan_active';
  }

  recoveryGateSatisfied = true;
  return 'proceed';
}
